/**
 * Pure geometry helpers for the personalisation live preview.
 *
 * SHARED FILE
 * VERSION: 1.5.0
 * UPDATED: 2026-11-03
 *
 * 1.0.0 was reserved for the first release of this feature to PRODUCTION, which happened on
 * 2026-09-17. The number now means "live", where 0.x meant "shipped nowhere real yet".
 *
 * A copy lives in the Engine repo at `public/vendor/mfyeb/personalisation-preview-geometry.js`,
 * where the admin placement editor and the offline order renderer both load it, so those surfaces
 * and the storefront cannot disagree about geometry. When you change behaviour here: bump VERSION
 * and UPDATED above, then copy the file across. Nothing detects a missed copy automatically,
 * because the two files live in different repositories, so those two lines are the only signal.
 * Compare them first whenever the editor and the storefront disagree about anything.
 *
 * Deliberately free of DOM access so the maths can be unit-tested in node. Text
 * measurement is injected as a `measure` callback — the browser supplies a canvas-based
 * one, tests supply a deterministic stub.
 *
 * All placement values in the config are normalised 0-1 fractions of the base image's
 * natural size. These helpers convert them into SVG *user units*, where the viewBox is
 * `0 0 <naturalWidth> <naturalHeight>`. That indirection is the whole point: one set of
 * stored coordinates renders correctly at mobile full-screen, desktop two-pane, and the
 * collapsed pinned strip, with no per-breakpoint data.
 *
 * ===========================================================================
 * CROSS-REPO CONTRACT: this file and Engine's placement editor MUST agree.
 * ===========================================================================
 * This module is the REFERENCE implementation of preview geometry. The same stored placement
 * values are also drawn by the admin editor in the Engine repo:
 *
 *   engine/Modules/ProductMaster/resources/views/livewire/product-master/personalisation/
 *     customization-builder.blade.php
 *
 * A merchandiser positions text there and trusts it predicts this. So the two must produce the
 * SAME RESULT for the same values. If you change how any field below is interpreted, change it
 * on the other side in the same PR, or the editor starts lying about what the customer sees.
 *
 * Fields both sides must honour identically:
 *   x, y, width, height  fractions of the base image, box top-left and size
 *   rotation             degrees clockwise, about the box CENTRE
 *   font_size            fraction of image HEIGHT (not the box, not em)
 *   letter_spacing       fraction of image HEIGHT (see the unit trap below)
 *   align                horizontal anchor within the box: left / center / right
 *                        (`start` / `end` accepted as SVG-flavoured aliases). The editor has no
 *                        control for it yet, so in practice every stored value is `center`.
 *   vertical_align       vertical anchor within the box: top / middle / bottom
 *   arc                  sweep angle in degrees, negative curves the other way
 *   max_width            fraction of image WIDTH, null means use the box width
 *   fit                  `none` disables shrinking AND the constant-size pass below. ANY other
 *                        value, including missing, shrinks to fit — the safe default, since an
 *                        unrecognised value must not let a long name run off the garment. Never
 *                        scales UP.
 *
 * Not a placement field, but it changes the drawn size, so both sides must supply it:
 *   charLimits           map of `text_line_key` -> the field's `char_limit`, straight out of the
 *                        config's `options.text_lines`. Build it with `charLimitsFrom(options)`.
 *                        It makes the drawn height INDEPENDENT of what is typed (see
 *                        `constantEmSize`). Omit it and the old per-keystroke shrink is back, so a
 *                        caller that forgets it disagrees with one that does not.
 *
 * HOW AGREEMENT IS MAINTAINED (as of 2026-08-17). The Engine editor does not implement any of this
 * a second time: it loads a byte-identical vendored copy of THIS file and calls `buildSvg()`,
 * rendering the result as a transparent overlay above the product photo, with its drag handles on
 * top. So changing a field here changes both places at once.
 *
 * That copy lives at engine/public/vendor/mfyeb/personalisation-preview-geometry.js and an Engine
 * test pins its md5. **If you change this file, re-vendor it in the same PR** (the copy command is
 * in that directory's README) or that test fails, which is the intended alarm.
 *
 * It was not always so: until 2026-08-12 the editor drew text with a positioned span and honoured
 * only x, y, width, height, rotation and font_size, silently ignoring the other six. A merchandiser
 * positioned against a preview that lied. Do not reintroduce a second implementation.
 *
 * UNIT TRAP: letter_spacing is a fraction of image height, NOT em. -0.1 becomes -275 user units
 * on a 2749px image and stacks every glyph on top of the next. The em equivalent is about -0.006.
 */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api; // node (tests)
  }
  root.PimPreviewGeometry = api; // browser
/**
 * `globalThis` first, and it matters outside a browser.
 *
 * Engine's package.json declares `"type": "module"`, so Node loads this file (vendored under that
 * repo) as ESM, where top-level `this` is undefined and the old `self : this` fallback threw
 * "Cannot set properties of undefined". Found while rasterising an order preview server-side.
 * globalThis exists in browsers, Node CJS and Node ESM alike, so this works in all three.
 */
})(typeof globalThis !== 'undefined' ? globalThis : (typeof self !== 'undefined' ? self : this), function () {
  'use strict';

  /**
   * MAKING THE TEXT LOOK LIKE REAL EMBROIDERY — read this before trying again.
   *
   * There is no texture here any more. A procedural SVG filter (turbulence, displacement,
   * specular bevel, contact shadow) lived in this module and in the modal's markup until
   * 2026-08-20 and was removed: it had also been silently dead since 82a518c0, which is its own
   * evidence about how much it contributed. The intended next attempt is a PHOTOGRAPHED thread
   * texture used as the glyph fill, which needs artwork rather than code. See
   * docs/plans/mfyeb-100-preview-tasks.md.
   *
   * Findings kept, because they cost days and they constrain any future attempt. All were
   * compared against magnified photographs of real stitching on actual products.
   *
   * THE ONE INSIGHT THAT MATTERS: at ACTUAL viewing size individual stitches are NOT
   * perceptible. Magnified 5x you can see satin banding, but on a real product page the eye only
   * reads a filled shape standing slightly proud of the fabric with a soft edge. EVERY attempt
   * to render visible stitch structure looked WORSE, because the real thing shows none at that
   * size, so stitch detail reads as fake.
   *
   * Rejected, with the reason:
   *  - per-letter SVG artwork — an exported letter is the SAME outline the font already has, so
   *    it changes nothing, and it loses the kerning/ligatures the joined-up scripts need.
   *  - stronger bead relief — filled in the counters of a/c/k; the word became blobs.
   *  - anisotropic turbulence bands — read as pencil hatching.
   *  - a synthetic satin <pattern> fill with per-thread gradients — flat, killed the roundness.
   *  - a REAL stitch texture sampled from the product photo and tiled as the glyph fill — the
   *    strokes are too thin to yield a pure-thread patch, so fabric gaps tile into a visible
   *    diamond artefact, and the sampled stitch direction is fixed while real strokes turn.
   *    Worth knowing before repeating it with commissioned artwork: the tiling artefact is the
   *    problem to solve, not the source of the pixels.
   *  - the product photo blended through the letters (overlay/soft-light) — washes out on
   *    near-white fabric; as a lighting bump map — flat and dark.
   *
   * Root cause common to all of them: they decorate a glyph SILHOUETTE, whereas real embroidery
   * is stitches whose direction follows each stroke's local geometry. A pattern has one fixed
   * angle; an SVG filter has no concept of direction at all.
   *
   * The only route to genuinely close is generating stitch paths per glyph (outline -> centreline
   * -> satin stitches across it -> thousands of shaded segments on a canvas), ~1.5-3 weeks. Not
   * approved. Note the commercial counter-argument: a photoreal preview raises customer
   * expectations and can increase returns when the delivered item differs.
   *
   * Also: judge at real size, never magnified. Three confident conclusions drawn from the 5x
   * crop turned out to be wrong.
   */

  /** Below this fraction of the target size the text is effectively unreadable. */
  const MIN_SHRINK_RATIO = 0.35;

  /**
   * PERSONALISATION METHOD TREATMENTS.
   *
   * The font decides the letterforms; the METHOD decides how those letterforms sit on the product,
   * and they are independent. The same name in the same font is a different object stitched in
   * satin thread, pressed in flock, printed in vinyl or debossed into leather, so a preview that
   * applies one treatment to all of them is wrong for all but one.
   *
   * Two dials per method, both deliberately cheap. There is no filter here, and there should not
   * be one: everything a filter was used for (fibre, specular sheen, the shadow where fabric
   * compresses) is invisible at real viewing size, which is what made every attempt look fake. See
   * the findings block below before adding anything.
   *
   *   strokeExpansion  how much wider the applied stroke is than the font's outline, as a fraction
   *                    of font size. Stroked in the glyph's OWN colour beneath the fill, so it
   *                    fattens the letterform without changing its shape or colour. 2% is a
   *                    measured ceiling: at 8% a delicate script's counters closed up entirely and
   *                    words became blobs, and 2% is where that starts to show.
   *
   *   tracking         extra space between letters, as a fraction of font size, ADDED to whatever
   *                    the placement stores. Not decoration: `strokeExpansion` grows every glyph
   *                    outwards by half the stroke on each side, so the visible GAP between two
   *                    letters shrinks by roughly the whole stroke width. Without compensation,
   *                    fattening the letterforms makes a name look cramped or joined up, which is
   *                    a change nobody asked for. Starting each method near its own expansion is
   *                    therefore principled rather than a taste call; flock is set higher than
   *                    that because the pilot reads better with it, which IS a taste call and is
   *                    meant to be tuned.
   *
   *   lighten          how far to lift the fill towards white, 0-1. PIM records the COLOURANT
   *                    (the spool, the pigment), and applied material photographs lighter than
   *                    that, so painting the recorded hex flat reads too dark against a real
   *                    product photo.
   *
   * WHAT IS ACTUALLY MEASURED, because only one thing is. On the forest-green cord backpack
   * personalised "Arlo": PIM records the colour as Grey-27 `#7A7D7C` (luma 124), while the
   * lettering in the product photograph averages `#899397` (luma 145) across its whole ink area.
   *
   * That number replaced an earlier one, and the correction is worth keeping. The first pass
   * measured the BRIGHTEST DECILE of the crop and got `#9ca2a7` (luma 161), which is the specular
   * highlights rather than the material, and it produced a value that rendered visibly too bright.
   * The reliable comparison is like for like: render our own SVG, then measure BOTH images the same
   * way over the same window. Doing that against a resvg render put us at luma 150 against the
   * photograph's 145, so the lift came down from 0.22 to 0.19 and now lands within a few luma of
   * the real product.
   *
   * Ink coverage in that same window: photograph 15.05%, our render 14.00%, and 12.75% with no
   * treatment at all. So the expansion is doing real work, and the remaining point of coverage is
   * ambiguous - the photograph's corduroy ridges push some fabric pixels over the ink threshold
   * too, so chasing it would be tuning against the fabric rather than the lettering.
   *
   * LIGHTEN IS 0 EVERYWHERE AS OF 2026-09-20. The history below is kept because it is the
   * measurement anyone restoring it would need.
   *
   * The 0.19 was measured once, on light-grey flock over a DARK corduroy backpack, and embroidery
   * inherited it untested. Lifting towards white gives the darkest colours the largest absolute
   * lift, so it failed hardest where it was never calibrated: navy #121447 drew at luma 69 against
   * a recorded 25, and a live product read as washed out. Only the measured grey (luma 124 -> 149)
   * was ever right.
   *
   * The field stays in the table so a real measurement can restore it per method. Measure a
   * DARK-material-on-PALE-fabric sample first: that is the case it was never calibrated for.
   *
   * THAT SAMPLE IS FLOCK, NOT EMBROIDERY. Zooming the same photo settles it: the name is smooth and
   * matte with no stitch structure, while the vehicles above it show unmistakable satin banding.
   * Embroidery carries the same value here on the ASSUMPTION that thread behaves similarly, which
   * is untested. Print keeps the recorded hex, because a vinyl or DTG film is a solid pigment layer
   * that should match its own swatch.
   *
   * THE OTHER CAVEAT: the measured sample is light material on DARK fabric. Lifting is least safe
   * in the opposite case, dark material on pale fabric, where it will read washed out rather than
   * brighter. Measure a navy-on-white sample the same way before trusting it there.
   *
   * `opacity` is how solid the drawn text is, and it applies on EVERY surface: the product page,
   * the cart thumbnail, the admin editor and the offline order render all read it from here. It
   * lived in the storefront's product-page component alone, which meant the other three drew at
   * full strength while the customer saw 90%, so a merchandiser judged a colour the customer never
   * got.
   *
   * Thread and fibre pile pick up the surface they sit on, so 0.9 reads as sitting ON the fabric.
   * Print is a solid pigment layer, which is the same reason it takes no lift and no expansion, so
   * it draws fully opaque. Deboss keeps 0.9 as the shipped behaviour; it has no colourant at all,
   * so the drawn text is a stand-in either way and nothing yet argues for a number.
   *
   * It is ELEMENT opacity, not per-paint: the satin stroke paints the SAME colour beneath the fill,
   * so per-paint opacity would let the stroke show through and darken every edge.
   *
   * The strokeExpansion figures are JUDGEMENT, not measurement: separating a material's spread from
   * the font's own weight needs the same font file the factory uses, which we do not have here.
   */
  const METHOD_TREATMENTS = {
    // Satin thread sits proud of the outline the font describes, and has a sheen. `lighten` is
    // inherited from flock's measurement rather than measured on thread, which is untested: satin
    // is glossier than a fibre pile, so if anything it wants MORE, not less.
    embroidery: { strokeExpansion: 0.015, tracking: 0.015, lighten: 0, opacity: 0.9 },
    // A raised fibre pile: soft edges, completely matte, and only slightly wider than the outline.
    // This is the one method with a measured `lighten` value.
    flock: { strokeExpansion: 0.01, tracking: 0.03, lighten: 0, opacity: 0.9 },
    // Vinyl / DTG / UV sits flat on the surface: the outline is the outline and the colour is the
    // colour.
    print: { strokeExpansion: 0, tracking: 0, lighten: 0, opacity: 1 },
    // Engraved or debossed: no colourant at all. The faint expansion stands in for the pressed
    // edge, but a convincing version needs the product's own colour shaded, which these two dials
    // cannot do. Treat it as indicative, and see the plan doc.
    deboss: { strokeExpansion: 0.008, tracking: 0.01, lighten: 0, opacity: 0.9 },
  };

  const DEFAULT_METHOD = 'embroidery';

  /**
   * Treatment for a method name, falling back to embroidery.
   *
   * The fallback is not laziness: embroidery is what the overwhelming majority of the catalogue
   * uses, and an unknown method means new PIM data reaching an older storefront, where drawing it
   * as embroidery is much better than drawing nothing.
   */
  function treatmentFor(method) {
    return METHOD_TREATMENTS[method] || METHOD_TREATMENTS[DEFAULT_METHOD];
  }

  /** Kept as a named export because the editor and the tests both refer to it. */
  const STITCH_EXPANSION = METHOD_TREATMENTS.embroidery.strokeExpansion;

  /**
   * Lift a hex colour towards white by `amount` (0-1).
   *
   * Towards white rather than by a multiplier so it saturates instead of overflowing: white thread
   * stays white rather than clipping oddly, and the darker the colour the more absolute lift it
   * gets, which is the right shape for a highlight.
   */
  function applyLighten(hex, amount) {
    if (!amount) return hex;

    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
    if (!m) return hex;

    const v = parseInt(m[1], 16);
    const lift = (c) => Math.round(c + (255 - c) * clamp(amount, 0, 1));
    const out = [lift((v >> 16) & 255), lift((v >> 8) & 255), lift(v & 255)]
      .map((c) => c.toString(16).padStart(2, '0'))
      .join('');

    return '#' + out;
  }

  /**
   * Widest baseline sweep, in degrees. Mirrors PersonalisationPreviewPlacement::ARC_LIMIT in
   * Engine — past a half turn an arced line overlaps itself, and no machine would sew it.
   * Clamped here as well because the storefront must not trust the config blindly.
   */
  const ARC_LIMIT = 180;

  /**
   * Cap-height/em ratio that `font_size` is expressed against.
   *
   * Roughly the cap ratio of a typical rounded display face, and deliberately an ANCHOR rather than
   * a measurement: a font matching it renders exactly as it did before cap-height normalisation
   * existed, so placements tuned by eye beforehand did not all shift. Changing this number rescales
   * every stored `font_size`, so do not tune it per product.
   */
  const REFERENCE_CAP_RATIO = 0.7;

  /**
   * Baseline-to-baseline distance of a multi-line message, in ems, for a font whose cap ratio is
   * REFERENCE_CAP_RATIO. The ceiling for every other font too: see `lineHeightFor`.
   */
  const LINE_HEIGHT = 1.2;

  /**
   * Letters `measureInkHeight` is asked about: a capital plus the tallest ascenders and deepest
   * descenders, so the answer is how far a row of ordinary text reaches above and below its
   * baseline. Exported so every caller measures the same string.
   */
  const INK_SAMPLE = 'Hbdfhklgjpqy';

  /** Clear space kept between one row's descenders and the next row's ascenders, in cap heights. */
  const ROW_GAP_CAP = 0.25;

  /**
   * A text line with a longer `char_limit` than this is a message, not a name, so it is wrapped
   * at word boundaries to fill its box (see `wrapToBox`). The storefront's `isLongLine()` uses the
   * same threshold to show a textarea instead of a single-line input.
   */
  const LONG_TEXT_CHAR_LIMIT = 50;

  /*
   * ORIENTATION, for anyone (or anything) picking this up cold
   * ==========================================================
   *
   * WHAT THIS IS. Pure geometry for the personalisation live preview. No DOM, no fetch, no
   * Alpine — so it runs under node for unit tests AND can be rasterised offline through resvg,
   * which is how the visual behaviour gets checked without a browser. Keep it that way: the
   * moment this touches `document`, both of those go away.
   *
   * THE CONTRACT. Engine sends a per-product JSON config (version 1.3). All geometry is
   * NORMALISED 0-1 fractions of the base image — never pixels — so one stored set of numbers
   * renders correctly at mobile full-screen, desktop two-pane and the collapsed pinned strip.
   * `computeRender()` multiplies them up and the result is used as an SVG viewBox coordinate.
   * The Engine side is Modules/ProductMaster/app/DTOs/PersonalisationConfigDTO.php.
   *
   * LAYERS. A step holds any number of placements. Each is either
   *   type 'text'   — a personalisation line, rendered in the chosen font and colour, and
   *                   optionally on a curved baseline (`arc`)
   *   type 'design' — a transparent PNG chosen from a select option, e.g. the backpack
   *                   embroidery designs, positioned by the same geometry
   * `computeRender()` returns `layers[]` in placement order. `text`, `arc` and `fit` are also
   * returned for the FIRST text layer only, because the single-line case is the common one and
   * predates layers; new code should read `layers`.
   *
   * THINGS THAT LOOK WRONG BUT ARE DELIBERATE.
   *  - `arc` is a SWEEP ANGLE, not a radius. A radius would need re-tuning every time the name
   *    length or font size changed; a sweep keeps the same visual curve. The radius is derived
   *    from the measured text width, which is why it can only be computed AFTER shrink-to-fit.
   *  - The arc path's length equals the text width exactly, so `startOffset="50%"` plus
   *    `text-anchor="middle"` centres the glyphs with nothing left over.
   *  - `max_width: null` means "use the box width". Null is not the same as 0.
   *  - Designs carry no asset dimensions anywhere. `preserveAspectRatio="xMidYMid meet"` does
   *    the fitting, so the design catalogue can grow without re-exporting products.
   *
   * WHAT IS NOT HERE YET. Bundles (the schema carries the component link, nothing renders it),
   * switching between preview angles, and Phase 2's WebGL/surface-curvature renderer. See the
   * memory notes `mfyeb100-live-preview-state` and `embroidery-preview-options-register`.
   *
   * A WARNING WORTH HEEDING. Judge how this LOOKS at true size — a 1200px product photo is
   * shown at roughly 390 CSS px on a phone, so a 1:1 render is about 3x too big. Days were lost
   * tuning a thread texture against a magnified crop; see the block below on what that cost.
   */

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const num = (v, fallback) => (typeof v === 'number' && isFinite(v) ? v : fallback);

  /**
   * Pick the step to render. Phase 1 always has exactly one, but the contract stores an
   * array so bundle components can be added later without re-exporting
   * every product config.
   */
  function resolveStep(preview, stepKey) {
    const steps = (preview && Array.isArray(preview.steps)) ? preview.steps : [];
    if (!steps.length) return null;
    if (stepKey) {
      const match = steps.find((s) => s && s.key === stepKey);
      if (match) return match;
    }
    return steps
      .slice()
      .sort((a, b) => num(a.sort_order, 0) - num(b.sort_order, 0))[0];
  }

  /**
   * The VIEWS a step offers: another photograph of the same product, each with its own placements.
   *
   * Engine exports every view under `step.views` AND repeats the primary one at step level, so a
   * storefront that predates views keeps working unchanged. This returns the list either way: a
   * config with no `views` yields one synthetic entry built from the step-level fields, so callers
   * never have to branch on config age.
   *
   * A view with no placement is dropped by the exporter, so anything here can be drawn.
   */
  function resolveViews(step) {
    if (!step) return [];

    const views = Array.isArray(step.views) ? step.views.filter(function (v) { return v && v.base_images; }) : [];

    if (views.length) return views;

    return step.base_images
      ? [{
        key: 'default',
        label: step.label || 'Front',
        sort_order: 0,
        base_images: step.base_images,
        placements: step.placements || [],
      }]
      : [];
  }

  /**
   * One view by key, falling back to the FIRST.
   *
   * The first is not an arbitrary choice: the exporter puts the primary view first, and the primary
   * view is the one the cart, the confirmation email and the stored order image all draw. So a
   * customer who never touches the view dots sees exactly what those surfaces will show.
   */
  function resolveView(step, viewKey) {
    const views = resolveViews(step);
    if (!views.length) return null;

    if (viewKey) {
      const match = views.find(function (v) { return v.key === viewKey; });
      if (match) return match;
    }

    return views[0];
  }

  /**
   * Resolve the base image for the selected variant, falling back to `default`.
   *
   * Most personalised products vary by SIZE, not colour (the reference robe's six
   * variants are age ranges), so `default` does the work for the majority and explicit
   * per-variant entries are the exception.
   *
   * BUSINESS RULE, confirmed by the client 2026-08-13: a product's COLOURS are NOT variants.
   * A colour range (bunnies, robes, towels) is a set of separate products, one per colour, tied
   * together only by the `custom.color_swatch_group` metaobject the theme reads on the PDP. Each
   * colourway is its own product with its own config, its own blank photo, its own placement, and
   * possibly a different font and thread colour. A swatch click NAVIGATES to that other product,
   * and inside the personalisation modal the colour cannot change at all. So this function will
   * never be asked to swap a base image because the customer picked another colour: by then it is
   * a different page with a different config. Do not build colour-switching on top of it.
   */
  function resolveBaseImage(step, variantId) {
    const images = (step && step.base_images) || {};
    if (variantId != null) {
      const keyed = images['variant:' + String(variantId)];
      if (keyed && keyed.url) return keyed;
    }
    return images.default && images.default.url ? images.default : null;
  }

  /**
   * WHICH FONT to draw with, given the customer's stored choice.
   *
   * Chosen value first, then the product's default, then whatever is available. A line can
   * legitimately name no font: the legacy widget does not always write one, a single-font product
   * has nothing to choose, and a line predating the feature carries nothing. In all of those the
   * product's default is what the workshop will use, so it is what should be drawn.
   *
   * Returns null only when the product has no fonts at all, which callers must treat as "do not
   * draw": the browser's default serif would misrepresent the order.
   *
   * Shared because both previews resolve this, and a cart line drawn in a different font from the
   * product page would be a contradiction the customer can see and we cannot.
   */
  function resolveFontChoice(fontChoices, chosenValue) {
    const choices = Array.isArray(fontChoices) ? fontChoices : [];

    return (
      choices.find((f) => f && f.value === chosenValue) ||
      choices.find((f) => f && f.is_default) ||
      choices[0] ||
      null
    );
  }

  /**
   * WHICH COLOUR to draw in, as a hex, before any method treatment.
   *
   * Order matters, and each step exists for a measured reason:
   *
   *  1. the customer's choice, when they had one;
   *  2. the product's DEFAULT recorded colour. A product stitched in one fixed colour has no
   *     entries in `font_colours` at all (the ivory bear hooded towel), so there is nothing to
   *     choose from and Engine sends `preview.default_colour_hex` for exactly that case. It also
   *     beats "the first swatch in the list", which is merely first;
   *  3. the FIRST colour the product actually offers. Merely first, and that is still better than
   *     inventing one: a colour the product does not sell is a worse thing to show a customer than
   *     an arbitrary one it does. This step exists because the case is real, not theoretical.
   *     Measured on staging 2026-08-27, "T-shirt cream - test" publishes
   *     `preview.default_colour_hex: null` AND offers black and forest green with NEITHER marked
   *     `is_default`, so steps 1 and 2 both miss while two perfectly good colours sit unused.
   *     `resolveFont` above has always fallen back to `choices[0]` for the same reason;
   *     this now matches it instead of diverging from it;
   *  4. a mid grey, deliberately NOT black: on cream terry black reads as an obvious mistake,
   *     whereas grey reads as a neutral placeholder. Reaching THIS means the product offers no
   *     colours at all and has no default either, which is a data problem worth seeing rather
   *     than hiding.
   *
   * The missing `is_default` in step 3 is a data gap worth correcting separately: the fallback
   * stops it looking broken, it does not make the choice right.
   */
  function resolveFill(colourChoices, chosenValue, defaultHex) {
    const choices = Array.isArray(colourChoices) ? colourChoices : [];

    const chosen = choices.find((c) => c && c.value === chosenValue);
    if (chosen && chosen.hex) return chosen.hex;

    const productDefault = choices.find((c) => c && c.is_default);
    if (productDefault && productDefault.hex) return productDefault.hex;

    if (defaultHex) return defaultHex;

    const first = choices.find((c) => c && c.hex);
    if (first) return first.hex;

    return '#4a4a4a';
  }

  /**
   * SVG text-anchor for a horizontal alignment.
   *
   * `start` / `end` are accepted alongside `left` / `right` because the cross-repo contract above
   * was written in SVG's vocabulary while this only ever honoured the CSS-flavoured one, so a value
   * copied from the documentation rendered centred instead of aligned. Both now work.
   */
  function textAnchor(align) {
    if (align === 'left' || align === 'start') return 'start';
    if (align === 'right' || align === 'end') return 'end';
    return 'middle';
  }

  /** SVG dominant-baseline for a vertical alignment. */
  function dominantBaseline(verticalAlign) {
    if (verticalAlign === 'top') return 'hanging';
    if (verticalAlign === 'bottom') return 'alphabetic';
    return 'central';
  }

  /**
   * Convert a placement into user-unit box geometry plus the anchor point the text is
   * positioned from and the rotation pivot (always the box centre, so rotation doesn't
   * drift the text off its intended spot).
   */
  function placementBox(placement, imgWidth, imgHeight) {
    const bx = num(placement.x, 0) * imgWidth;
    const by = num(placement.y, 0) * imgHeight;
    const bw = num(placement.width, 0) * imgWidth;
    const bh = num(placement.height, 0) * imgHeight;

    const align = placement.align || 'center';
    const vAlign = placement.vertical_align || 'middle';

    // `start` / `end` are honoured here as well as in textAnchor(). They used to be accepted by
    // textAnchor only, so `align: 'start'` drew start-aligned text anchored at the box CENTRE and
    // the line ran out of the right-hand side. Latent, because every stored value is `center`.
    let ax = bx + bw / 2;
    if (align === 'left' || align === 'start') ax = bx;
    else if (align === 'right' || align === 'end') ax = bx + bw;

    let ay = by + bh / 2;
    if (vAlign === 'top') ay = by;
    else if (vAlign === 'bottom') ay = by + bh;

    return {
      x: bx, y: by, width: bw, height: bh,
      imgWidth, imgHeight,
      anchorX: ax, anchorY: ay,
      pivotX: bx + bw / 2, pivotY: by + bh / 2,
      rotation: num(placement.rotation, 0),
      textAnchor: textAnchor(align),
      dominantBaseline: dominantBaseline(vAlign),
      letterSpacing: num(placement.letter_spacing, 0) * imgHeight,
    };
  }

  /**
   * Resolve the font size in user units, applying `fit`.
   *
   * `fit: "shrink"` renders at `font_size` and scales DOWN if the text overflows the box
   * width — never up. This mirrors real embroidery: the machine has a fixed maximum
   * height and long names come out smaller. It also keeps the admin's job predictable —
   * set the size for a typical name and long ones degrade instead of overflowing.
   *
   * @param {function} measure (text, fontSizeUserUnits) => width in user units
   */
  /**
   * Cap-height/em ratio of the font being drawn, used to keep different fonts the same VISUAL size.
   *
   * `font_size` is an em size, but em is not a visible quantity: two fonts at the same em can have
   * very different cap heights, so switching font used to visibly resize the name. That reads as a
   * bug to a customer, and it also means a merchandiser tunes the size against one font while
   * customers see another. The reference Magento engraving module solves it the same way, by probing
   * a capital's ink height and scaling from that (see `normalizeFontSizeForVisualHeight`).
   *
   * The measurement is injected, because only the host can do it: the browser has canvas
   * `actualBoundingBoxAscent`, the offline renderer has to read the font. When no measurer is
   * supplied the ratio falls back to REFERENCE_CAP_RATIO, which makes the normalisation a no-op and
   * preserves the old behaviour rather than guessing.
   */
  function capHeightRatio(measureCapHeight) {
    if (typeof measureCapHeight !== 'function') return REFERENCE_CAP_RATIO;
    const probe = 100;
    const h = num(measureCapHeight(probe), 0);
    if (!(h > 0)) return REFERENCE_CAP_RATIO;
    // Guard against a nonsense measurement (a font that failed to load reports the fallback
    // face's metrics) rather than letting it scale the text into oblivion.
    const ratio = h / probe;
    return ratio >= 0.3 && ratio <= 1.2 ? ratio : REFERENCE_CAP_RATIO;
  }

  /**
   * Baseline-to-baseline distance of a multi-line message, in ems of the DRAWN size (MFYEB-212).
   *
   * The drawn em is scaled by `normalisedEmSize` so the capitals come out at the stored height. A
   * flat LINE_HEIGHT em pitch was scaled with it, so a short-capped font got the same visual
   * letters but rows pushed apart: ApothecarySerif (caps 0.33 em) was drawn at 2.2x the em and
   * read as double-spaced. So the pitch is expressed in cap heights instead, which is what makes it
   * look the same in every font, and then bounded on both sides:
   *
   *  - never below the font's own reach plus ROW_GAP_CAP, measured on INK_SAMPLE. A script face
   *    can have short capitals and long loops (Alright_Scrapbooky, Degenerated), and a pitch from
   *    the capitals alone would make its rows collide.
   *  - never above LINE_HEIGHT, so no font is spaced wider than it was before this existed.
   *
   * Without `measureInkHeight` the reach is unknown, so the pitch stays at LINE_HEIGHT exactly as
   * before: a caller that has not learnt about it draws what it always drew.
   */
  /**
   * Height a block of rows is budgeted, in ems: the pitch between rows plus one full LINE_HEIGHT
   * row. With the default pitch that is exactly `rows * LINE_HEIGHT`, the budget before
   * `lineHeightFor` existed. Keeping a whole LINE_HEIGHT for the last row matters because rows are
   * centred with dominant-baseline="central", which centres the font's EM BOX, not its letters: a
   * face whose em is much taller than its ink (ApothecarySerif) is drawn lower than its box, and
   * the old pitch's slack is what kept that inside. Tightening the pitch must not spend it.
   */
  function blockHeightEm(rowCount, lineHeight) {
    const pitch = lineHeight || LINE_HEIGHT;
    return Math.max(rowCount - 1, 0) * pitch + LINE_HEIGHT;
  }

  function lineHeightFor(measureCapHeight, measureInkHeight) {
    if (typeof measureInkHeight !== 'function') return LINE_HEIGHT;
    const probe = 100;
    const ink = num(measureInkHeight(probe), 0) / probe;
    if (!(ink > 0) || ink > 3) return LINE_HEIGHT;
    const cap = capHeightRatio(measureCapHeight);
    const fromCaps = LINE_HEIGHT * (cap / REFERENCE_CAP_RATIO);
    return Math.min(LINE_HEIGHT, Math.max(fromCaps, ink + ROW_GAP_CAP * cap));
  }

  /**
   * Em size that renders `font_size` worth of CAP HEIGHT in this font.
   *
   * Anchored to REFERENCE_CAP_RATIO rather than treating `font_size` as cap height directly, so
   * values tuned before this existed keep rendering at roughly the size they were tuned to. A font
   * whose cap ratio equals the reference is unchanged; a short-capped font is scaled up to match.
   *
   * ---------------------------------------------------------------------------------------------
   * WE STORE font_size SEPARATELY FROM height. The reference project DERIVES IT FROM THE BOX.
   * ---------------------------------------------------------------------------------------------
   * Magebit's Magento CustomEngraving module (see the notes in `engraving-reference-implementation`)
   * stores no font size for its overlay at all. It computes one at render time:
   *
   *     baseFontSize = max(MIN_FONT_SIZE, box.heightPx * visualScale)
   *
   * i.e. the box height IS the size, scaled by the same measured cap ratio we use above. Their box
   * is percentages (0-100) where ours is fractions (0-1), which is only cosmetic; this is the real
   * difference.
   *
   * WHY WE KEPT font_size separate: a placement box is a bounding AREA, not a tight wrapper. That
   * is what makes `align` and `vertical_align` meaningful (put a short name top-left of a region)
   * and what lets `max_width` limit wrapping independently of where the text sits. Their model
   * cannot express any of that.
   *
   * WHAT IT COSTS US: two fields that can disagree. The editor sets font_size from the box height on
   * resize, so it usually behaves like theirs, but a merchandiser can then change one without the
   * other and the box stops describing the text.
   *
   * IF WE EVER WANT THEIR MODEL: it is a narrow change, not a rewrite. Make this function return
   * `box.height * imgHeight * (REFERENCE_CAP_RATIO / capHeightRatio(...))`, ignore the stored
   * `font_size`, and drop the field from the editor. The contract stays valid because `font_size`
   * would simply become unread rather than removed. The thing to decide FIRST is whether anyone
   * needs text smaller than its box; if the answer is no, their model is strictly simpler and
   * removes a whole class of "why does the box not match the text" confusion.
   */
  function normalisedEmSize(placement, box, measureCapHeight) {
    const nominal = num(placement.font_size, 0.05) * num(box.imgHeight, 0);
    return nominal * (REFERENCE_CAP_RATIO / capHeightRatio(measureCapHeight));
  }

  /**
   * Characters the constant-size pass measures against.
   *
   * The whole uppercase alphabet, used for its AVERAGE ADVANCE WIDTH rather than as a literal
   * string, so the reference is stable at any character limit (a 3-character limit measured
   * against the literal prefix "ABC" would inherit whatever those three letters happen to be).
   *
   * Uppercase, because an all-caps name is the widest thing a customer can realistically type, and
   * some do. Not `W` repeated: `W` is roughly twice the average advance, so sizing against it would
   * draw every name about half the height the merchandiser tuned, which looks wrong against the
   * real product. The average-uppercase reference is the widest REALISTIC case, not the widest
   * possible one, so `WWWWWWWWWW` can still overrun a little — the shrink pass below stays in place
   * as the safety net for exactly that.
   */
  const FIT_REFERENCE_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  /**
   * Character limits keyed by text line, in the shape `computeRender` wants, from a config's
   * `options` block.
   *
   * Here rather than in each caller because there are four of them (the product page, the cart
   * line, the admin editor and the offline order renderer) and a caller that skips it silently
   * draws a different size from the ones that do not.
   */
  function charLimitsFrom(options) {
    const out = {};
    for (const line of (options && options.text_lines) || []) {
      const limit = line && Number(line.char_limit);
      if (line && line.key && limit > 0) out[line.key] = limit;
    }
    return out;
  }

  /**
   * Em size to draw at, settled against the LONGEST NAME THE FIELD ACCEPTS rather than against
   * what is typed.
   *
   * ---------------------------------------------------------------------------------------------
   * TIP: THIS IS WHY THE PREVIEW NO LONGER RESIZES AS YOU TYPE, AND HOW TO PUT THAT BACK
   * ---------------------------------------------------------------------------------------------
   * HOW IT IS (since 2026-08-25). The size depends on the placement, the font and the field's
   * `char_limit` — never on the customer's text. So "A" and "ALEXANDRIA" draw at the same height,
   * and because the size was chosen so that a limit-length name fits, nothing a customer can type
   * overruns the box. `fitFontSizeRaw` still runs afterwards and is still the thing that reports
   * `fits` / `clamped`, but on a sane config it now never actually shrinks anything.
   *
   * HOW IT WAS, and how to get back to it: this function is the only difference. Return `nominal`
   * unconditionally (or stop passing `charLimits` into `computeRender`) and the old behaviour is
   * back exactly — the text renders at the stored `font_size` and each keystroke that overruns the
   * box shrinks it a little further. That was deliberate too: it mirrors the machine, where a long
   * name comes out smaller. What it cost was a preview that changed height while the customer
   * typed, and a one- or two-letter name drawn at the full stored size, which reads as oversized
   * next to the same product with a longer name.
   *
   * WHAT IT COSTS AS IT STANDS. A generous `char_limit` on a small box now shrinks EVERY name on
   * that product, including short ones, because the limit is what the size is chosen for. So a
   * limit that nobody would ever fill is no longer harmless: it is a visible size reduction. The
   * fix for that is on the config side, reconciling each field's limit with the box it is drawn in.
   *
   * SIZE IS UNCHANGED when the limit already fits at the stored `font_size`, which is the common
   * case, so this does not quietly rescale the catalogue.
   *
   * The MIN_SHRINK_RATIO floor applies here as well, so an impossible config (a 20-character limit
   * in a box a third of the image wide) stops at a readable size instead of vanishing. Past that
   * floor the shrink pass can still take another 35% off the typed text, which compounds — that is
   * only reachable on a config that could never be stitched as configured, and `clamped` is set so
   * it surfaces rather than hides.
   */
  function constantEmSize(opts) {
    const { nominal, boxWidth, fit, measure } = opts;
    const limit = Math.floor(num(opts.charLimit, 0));

    // `fit: 'none'` is an explicit "draw it exactly as stored", so it opts out of this too.
    if (fit === 'none' || !(limit > 0) || !(nominal > 0) || !(boxWidth > 0)) return nominal;
    if (typeof measure !== 'function') return nominal;

    const alphabet = measure(FIT_REFERENCE_ALPHABET, nominal);
    if (!(alphabet > 0)) return nominal;

    // Average advance times the limit, plus the tracking between them, the same way
    // fitFontSizeRaw accounts for spacing.
    const gaps = Math.max(limit - 1, 0);
    const width =
      (alphabet / FIT_REFERENCE_ALPHABET.length) * limit +
      gaps * (num(opts.trackingEm, 0) * nominal + num(opts.spacingFixed, 0));

    if (!(width > 0) || width <= boxWidth) return nominal;

    return nominal * Math.max(boxWidth / width, MIN_SHRINK_RATIO);
  }

  /**
   * =============================================================================================
   * THE FONT SIZE POLICY, ALL OF IT, IN ONE FUNCTION. Change the size rule HERE.
   * =============================================================================================
   * Everything that decides how big the text is drawn happens in these two steps and nowhere else.
   * `fitFontSize` below is only the plumbing that hands the answer to the shrink pass.
   *
   * STEP 1, THE BOX HEIGHT sets the letter height. `font_size` is stored as a fraction of the
   * image height and the editor derives it from the box height on every resize
   * (`fontSizeForBoxHeight`), so in practice the box height IS the size. `normalisedEmSize` then
   * corrects for the font's cap ratio, so two fonts at the same setting look the same size rather
   * than merely sharing an em.
   *
   * STEP 2, THE BOX WIDTH caps it. A box is the real available space on BOTH axes, so a name that
   * is short enough to fit the height can still be too long to fit across. `constantEmSize` takes
   * the size down until a name of the field's full `char_limit` fits the width. Because it measures
   * the LIMIT and not the customer's text, the answer does not move while somebody types: one
   * letter and ten letters draw identically. That is the whole reason it exists.
   *
   * WHAT THAT ASKS OF THE MERCHANDISER, and it is the honest deal: the box has to be the real
   * stitching zone, and a zone that takes a long name is LONG AND THIN. Draw a tall box for an
   * 18-character field and the width cap will pull the text far below the box height, which looks
   * wrong and is telling the truth — 18 letters at that height were never going to fit. The fix is
   * always the box, never the number.
   *
   * THE KNOBS, in the order you are likely to want them:
   *  - `REFERENCE_CAP_RATIO`   what `font_size` means as a cap height. Changing it rescales every
   *                            stored value in the catalogue, so do not tune it per product.
   *  - `FIT_REFERENCE_ALPHABET` the characters step 2 measures. Widen it and every name shrinks.
   *  - `MIN_SHRINK_RATIO`      the floor both steps stop at, so an impossible config stays legible.
   *  - `max_width`             per placement, a tighter width for step 2 than the box itself.
   *  - `fit: 'none'`           per placement, opt out of step 2 AND the shrink pass entirely.
   *
   * TO GO BACK TO SIZING FROM THE TYPED TEXT (the pre-2026-08-26 behaviour): return
   * `fromHeight` and ignore step 2. The text then renders at the stored size and each keystroke
   * that overruns the box shrinks it a little further, which mirrors the machine but means the
   * preview changes height while the customer types.
   *
   * @returns {{em: number, fromHeight: number, limitedByWidth: boolean}}
   */
  function resolveEmSize(placement, box, opts) {
    const fromHeight = normalisedEmSize(placement, box, opts.measureCapHeight);

    const em = constantEmSize({
      nominal: fromHeight,
      boxWidth: opts.boxWidth,
      charLimit: opts.charLimit,
      fit: placement.fit,
      measure: opts.measure,
      trackingEm: opts.trackingEm,
      spacingFixed: opts.spacingFixed,
    });

    return { em: em, fromHeight: fromHeight, limitedByWidth: em < fromHeight };
  }

  /**
   * Where the ink lands INSIDE the box, as fractions of the box's own offsets.
   *
   * This is what a DRAG CLAMP has to constrain, and it is why one existed that felt wrong: the
   * editor limited the box's EDGES to the artwork, so a 0.4-wide box stopped dead at x = 0.6 while
   * the operator could plainly see slack inside it. The box is a bounding area; the text sits at
   * this point in it. Constrain this and the limit finally matches where the stitching goes.
   *
   * Derived by asking placementBox itself, on a 1x1 image with the box at the origin, so the clamp
   * and the renderer can never disagree about what `align` means. Engine's
   * PersonalisationPreviewPlacement::anchorOffsets() mirrors it in PHP for the server-side clamp;
   * the two must stay in step or saving silently pulls a dragged box back.
   *
   * @returns {{x: number, y: number}} offsets from the box's top-left, as image fractions
   */
  function anchorOffset(placement) {
    const p = placement || {};
    const box = placementBox({
      x: 0,
      y: 0,
      width: num(p.width, 0),
      height: num(p.height, 0),
      align: p.align,
      vertical_align: p.vertical_align,
    }, 1, 1);

    return { x: box.anchorX, y: box.anchorY };
  }

  function fitFontSize(placement, box, text, measure, measureCapHeight, trackingEm, charLimit) {
    const boxWidth = maxLineWidth(placement, box);
    // The placement's own letter_spacing, already in user units courtesy of placementBox.
    const spacingFixed = box.letterSpacing;

    const size = resolveEmSize(placement, box, {
      boxWidth: boxWidth,
      charLimit: charLimit,
      measure: measure,
      measureCapHeight: measureCapHeight,
      trackingEm: trackingEm,
      spacingFixed: spacingFixed,
    });

    const result = fitFontSizeRaw({
      text,
      targetSize: size.em,
      boxWidth,
      fit: placement.fit,
      measure,
      trackingEm: trackingEm,
      spacingFixed,
    });

    // Reported so a caller can tell "the box width decided this" from "the typed text did", which
    // is the difference between a box that needs redrawing and a name that is simply long.
    result.limitedByWidth = size.limitedByWidth;
    result.emFromHeight = size.fromHeight;

    return result;
  }

  /**
   * Measurement-agnostic core of {@link fitFontSize}, kept separate for testing.
   *
   * Also reports `textWidth` — the rendered width at the FINAL font size. An arced baseline
   * needs it: the stored `arc` is a sweep angle, and turning that into a radius requires
   * knowing how long the line actually is, which is only settled after shrinking.
   */
  function fitFontSizeRaw(opts) {
    const { text, targetSize, boxWidth, fit, measure } = opts;
    const result = { fontSize: targetSize, scale: 1, fits: true, clamped: false, textWidth: 0 };

    if (!text || !targetSize || !boxWidth || typeof measure !== 'function') return result;

    /**
     * Letter spacing counts towards the width, and used not to.
     *
     * `measure` is a canvas measureText, which knows nothing about the letter-spacing attribute
     * the renderer then applies, so the fit systematically underestimated the line. Harmless while
     * every product stored 0; not harmless the moment a method adds tracking of its own, where a
     * nine-letter name at 3% would run a quarter of a font size past where the fit thought it
     * ended.
     *
     * `trackingEm` scales with the font size, so the proportional shrink below stays exact for it.
     * `spacingFixed` (the placement's own value, a fraction of image HEIGHT, see the unit trap at
     * the top of this file) does NOT scale, so including it makes the shrink slightly conservative
     * rather than exact. That is the safe direction, and it is nil in practice because nothing
     * writes that field today.
     */
    const gaps = Math.max(text.length - 1, 0);
    const spacingAtTarget = gaps * (num(opts.trackingEm, 0) * targetSize + num(opts.spacingFixed, 0));

    const width = measure(text, targetSize) + spacingAtTarget;
    if (!(width > 0)) return result;

    result.textWidth = width;

    // Only an explicit 'none' disables shrinking. Deliberately not `fit === 'shrink'`: a config
    // with the field missing or misspelled must still shrink, because the alternative is a name
    // running off the edge of the product.
    if (fit === 'none') {
      result.fits = width <= boxWidth;
      return result;
    }

    if (width <= boxWidth) return result;

    let scale = boxWidth / width;
    if (scale < MIN_SHRINK_RATIO) {
      scale = MIN_SHRINK_RATIO;
      result.clamped = true; // box is too small for this text; surface it rather than hide it
    }
    result.scale = scale;
    result.fontSize = targetSize * scale;
    // Text width scales linearly with font size, so this needs no second measurement. When
    // `clamped`, the text is deliberately WIDER than the box — the arc must follow the text,
    // not the box, or the curve would not line up with the glyphs.
    result.textWidth = width * scale;
    result.fits = !result.clamped;
    return result;
  }

  /**
   * Width that `fit` measures against.
   *
   * `max_width` (a fraction of the image WIDTH) overrides the box width when set. Deliberate:
   * the box also positions and vertically centres the text, so a merchandiser needs a way to
   * say "wide box, tighter wrap limit" — and the machine constraint is expressed the same
   * way, as a maximum field width independent of where the design sits. Null falls back to
   * the box, which is what the geometry did before the field existed.
   */
  function maxLineWidth(placement, box) {
    const explicit = placement && placement.max_width;
    if (typeof explicit === 'number' && isFinite(explicit) && explicit > 0) {
      return explicit * num(box.imgWidth, 0);
    }
    return box.width;
  }

  /**
   * Baseline path for arced text, or null when the placement is straight.
   *
   * `arc` is the total SWEEP of the line in degrees, signed: positive curves the text upwards
   * (a smile, text on the outside of the circle), negative downwards. A sweep rather than a
   * radius so the curve looks the same whatever the name's length or size — a stored radius
   * would need re-tuning for every name.
   *
   * The arc's LENGTH is made equal to the text width, so `startOffset="50%"` with
   * `text-anchor="middle"` centres the glyphs on it exactly with nothing left over.
   *
   * The path is also shifted vertically by half its own bow, so that turning the curve up or
   * down keeps the text visually centred on the anchor instead of sliding out of the box.
   *
   * @param {number} textWidth rendered width of the text, in user units
   */
  /**
   * The CIRCLE an arced baseline lies on, rather than the SVG path string that draws it.
   *
   * `arcPath()` returns a `d` attribute, which is all a browser needs: `<textPath>` distributes the
   * glyphs along it. Anything that has to place glyphs ITSELF needs the circle instead — the factory
   * production SVG converts each glyph to outlines, so it has to find the point and the tangent
   * angle for every one. Exposed here, and used by `arcPath()` below, so there is ONE definition of
   * where the baseline goes and the two cannot drift.
   *
   * Geometry, for anyone deriving it again: the chord is horizontal and `textWidth` long along the
   * arc, so `radius = textWidth / sweep`. The ends sit at `y = cy - sign * radius * cos(half)` and
   * the middle at `y = cy - sign * radius`, which is `bow` higher — that is what makes a positive
   * arc a smile. `sign` is +1 for a smile (centre BELOW the text) and -1 for a frown.
   *
   * @returns {{cx: number, cy: number, radius: number, sign: number, half: number, chordY: number,
   *            halfChord: number, bow: number, sweepDeg: number}|null} null when the placement is straight
   */
  function arcCircle(placement, box, textWidth) {
    const arcDeg = clamp(num(placement && placement.arc, 0), -ARC_LIMIT, ARC_LIMIT);
    if (!arcDeg || !(textWidth > 0)) return null;

    const sign = arcDeg < 0 ? -1 : 1;
    const sweep = Math.abs(arcDeg) * (Math.PI / 180);
    const radius = textWidth / sweep;
    const half = sweep / 2;
    const halfChord = radius * Math.sin(half);
    const bow = radius * (1 - Math.cos(half));

    // Keep the alignment point on the anchor: centre the chord for 'center', put its left end
    // on the anchor for 'left', its right end for 'right'. The glyphs then always sit at 50%
    // along the path, which keeps the textPath maths identical in all three cases.
    const align = (placement && placement.align) || 'center';
    let cx = box.anchorX;
    if (align === 'left') cx += halfChord;
    else if (align === 'right') cx -= halfChord;

    const chordY = box.anchorY + sign * (bow / 2);

    return {
      cx: cx,
      // Centre of the circle: below the text for a smile, above it for a frown.
      cy: chordY + sign * radius * Math.cos(half),
      radius: radius,
      sign: sign,
      half: half,
      chordY: chordY,
      halfChord: halfChord,
      bow: bow,
      sweepDeg: arcDeg,
    };
  }

  /**
   * Where one glyph sits on an arced baseline, and how far to rotate it.
   *
   * `distanceFromStart` is measured along the arc from the first glyph, so pass a glyph's MIDPOINT
   * to match what `<textPath>` does: it centres each glyph on its own advance and rotates it to the
   * tangent there.
   *
   * The angle is the tangent's, in degrees, ready for an SVG `rotate()`. It equals the sweep angle
   * from the middle of the arc, which falls out of the parameterisation: the tangent to a circle at
   * angle phi makes exactly phi with the horizontal.
   *
   * @returns {{x: number, y: number, angle: number}}
   */
  function arcPointAt(circle, distanceFromStart, textWidth) {
    // Signed distance from the MIDDLE of the line, which is where the arc's apex is.
    const fromMiddle = num(distanceFromStart, 0) - num(textWidth, 0) / 2;
    const phi = circle.radius ? fromMiddle / circle.radius : 0;

    return {
      x: circle.cx + circle.radius * Math.sin(phi),
      y: circle.cy - circle.sign * circle.radius * Math.cos(phi),
      angle: circle.sign * phi * (180 / Math.PI),
    };
  }

  function arcPath(placement, box, textWidth) {
    const circle = arcCircle(placement, box, textWidth);
    if (!circle) return null;

    const { radius, sign, halfChord, bow } = circle;
    const cx = circle.cx;
    const endsY = circle.chordY;
    const startX = cx - halfChord;
    const endX = cx + halfChord;

    // Large-arc flag is always 0 because the sweep is capped at half a turn. The sweep flag
    // decides which way the bow points: 1 draws over the top (a smile), 0 under (a frown).
    const d =
      'M ' + round(startX, 3) + ' ' + round(endsY, 3) +
      ' A ' + round(radius, 3) + ' ' + round(radius, 3) +
      ' 0 0 ' + (sign > 0 ? 1 : 0) + ' ' + round(endX, 3) + ' ' + round(endsY, 3);

    return { d, radius: round(radius, 3), sweep: circle.sweepDeg, bow: round(bow, 3), circle: circle };
  }

  /**
   * viewBox for the collapsed ("pinned and scrolled") preview.
   *
   * The mobile design sticks the preview to the top and shrinks it on scroll while keeping
   * the personalised text visible, so the crop needs an anchor. We keep the full garment
   * width and crop vertically around the focus point, clamped to the image so the band
   * never runs off the artwork.
   *
   * @param {number} aspect width/height of the collapsed container
   */
  function collapsedViewBox(imgWidth, imgHeight, focus, aspect) {
    if (!(imgWidth > 0) || !(imgHeight > 0) || !(aspect > 0)) {
      return { x: 0, y: 0, width: imgWidth || 0, height: imgHeight || 0 };
    }
    const height = Math.min(imgHeight, imgWidth / aspect);
    const focusY = clamp(num(focus && focus.y, 0.5), 0, 1) * imgHeight;
    const y = clamp(focusY - height / 2, 0, imgHeight - height);
    return { x: 0, y, width: imgWidth, height };
  }

  /** Default focus point: the centre of the first placement's box. */
  function defaultFocus(step) {
    const p = step && Array.isArray(step.placements) ? step.placements[0] : null;
    if (!p) return { x: 0.5, y: 0.5 };
    return {
      x: clamp(num(p.x, 0) + num(p.width, 0) / 2, 0, 1),
      y: clamp(num(p.y, 0) + num(p.height, 0) / 2, 0, 1),
    };
  }

  const formatViewBox = (vb) =>
    [round(vb.x), round(vb.y), round(vb.width), round(vb.height)].join(' ');

  function escapeText(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function escapeAttr(s) {
    return escapeText(s).replace(/"/g, '&quot;');
  }


  /**
   * Resolve everything needed to draw one frame of the preview.
   *
   * This is the single source of truth for the maths. The storefront applies the result to
   * persistent SVG nodes via setAttribute (so the base image element is never recreated on
   * keystroke); {@link buildSvg} renders the same result to a string for tests and offline
   * rasterisation. Both paths therefore exercise identical geometry.
   *
   * @returns {{viewBox: string, image: object, text: object|null, fit: object, ariaLabel: string}}
   */
  /**
   * A `type: "design"` placement: a transparent PNG chosen from a select option, drawn at the
   * placement's box.
   *
   * No asset dimensions appear anywhere in the contract, because
   * `preserveAspectRatio="xMidYMid meet"` scales the artwork to fit the box and centres the
   * remainder. That matters: designs are a catalogue that grows, and carrying each one's pixel
   * size in the config would mean re-exporting every product that offers it.
   *
   * `arc` is ignored for designs — a curved baseline is a text concept, and bending a bitmap
   * needs a mesh warp, which is Phase 2 territory.
   */
  function designOverlay(placement, box, url) {
    if (!url) return null;
    return {
      kind: 'design',
      url: url,
      x: round(box.x),
      y: round(box.y),
      width: round(box.width),
      height: round(box.height),
      preserveAspectRatio: 'xMidYMid meet',
      transform: box.rotation
        ? 'rotate(' + round(box.rotation, 3) + ' ' + round(box.pivotX) + ' ' + round(box.pivotY) + ')'
        : null,
    };
  }

  /**
   * Text a placement should draw.
   *
   * `texts` is keyed by `text_line_key`, so a step with several fields puts the right value in each.
   * `text` remains the single-line shorthand.
   *
   * ---------------------------------------------------------------------------------------------
   * AN EMPTY FIELD DRAWS NOTHING. It used to draw the OTHER FIELD'S TEXT.
   * ---------------------------------------------------------------------------------------------
   * This used to fall through to `text` whenever the keyed value was missing, which sounds harmless
   * and was not: a caller only lists the fields that have something in them (both the storefront's
   * `allTexts()` and the cart line's `data-texts`, which is built from the line-item properties that
   * are not blank). So a customer who filled the first field and left the second empty had the FIRST
   * field's name drawn in the second field's box, on the product page and on the cart line alike.
   *
   * The distinction that fixes it: a placement WITH a key whose value is absent from a supplied map
   * is EMPTY. Only a placement with no key at all, or a caller that supplied no map whatsoever, means
   * "use the single-line shorthand".
   */
  function textForPlacement(placement, o) {
    const key = placement && placement.text_line_key;

    if (key) {
      if (o.texts && Object.prototype.hasOwnProperty.call(o.texts, key)) {
        return String(o.texts[key] == null ? '' : o.texts[key]).trim();
      }

      // No map at all: the caller is using the single-line shorthand and this is the one field.
      if (!o.texts) {
        return String(o.text == null ? '' : o.text).trim();
      }

      // A map was supplied and this field is not in it, so the customer left it blank.
      return '';
    }

    return String(o.text == null ? '' : o.text).trim();
  }

  /** One text placement's font, colour and method: `o.styleFor(placement)` overrides, else the call-wide values. */
  function textStyleFor(placement, o) {
    const own = (typeof o.styleFor === 'function' && o.styleFor(placement)) || {};

    return {
      fontFamily: own.fontFamily || o.fontFamily || '',
      fill: own.fill || o.fill || '#000000',
      method: own.method || o.method || DEFAULT_METHOD,
      measure: own.measure || o.measure,
      measureCapHeight: own.measureCapHeight || o.measureCapHeight,
      measureInkHeight: own.measureInkHeight || o.measureInkHeight,
    };
  }

  /**
   * Character limit that settles this placement's constant size.
   *
   * Placement first so a merchandiser can eventually tighten one box without changing the field
   * every product shares, then the text line's own limit.
   */
  function charLimitFor(placement, o) {
    const own = placement && Number(placement.char_limit);
    if (own > 0) return own;
    const key = placement && placement.text_line_key;
    const limits = o.charLimits || {};
    const shared = key ? Number(limits[key]) : 0;
    return shared > 0 ? shared : 0;
  }

  /**
   * A textarea message keeps the customer's line breaks, so it is drawn as one row per line.
   * Blank lines are kept because they are spacing the customer chose.
   */
  function splitLines(value) {
    return String(value).split(/\r?\n/).map(function (line) { return line.replace(/\s+$/, ''); });
  }

  /** Shrinks a stack of lines until it fits the box on both axes, with no floor so it never spills out. */
  function fitLinesToBox(fit, placement, box, lineCount, lineHeight) {
    if (lineCount < 2 || placement.fit === 'none' || !(fit.fontSize > 0)) return fit;

    const boxWidth = maxLineWidth(placement, box);
    const widthCap = boxWidth > 0 && fit.textWidth > boxWidth ? fit.fontSize * (boxWidth / fit.textWidth) : Infinity;
    const heightCap = box.height > 0 ? box.height / blockHeightEm(lineCount, lineHeight) : Infinity;
    const cap = Math.min(widthCap, heightCap);
    if (fit.fontSize <= cap) return fit;

    const ratio = cap / fit.fontSize;

    return Object.assign({}, fit, {
      fontSize: cap,
      scale: fit.scale * ratio,
      textWidth: fit.textWidth * ratio,
      fits: true,
      clamped: false,
      limitedByHeight: heightCap <= widthCap,
    });
  }

  /**
   * Wraps a long message at word boundaries and finds the largest size at which it fits the box.
   *
   * A message typed without line breaks used to be drawn as one row, which stopped shrinking at
   * MIN_SHRINK_RATIO and ran far outside the box. Here the rows are re-broken at every candidate
   * size, so the message fills the box and only gets smaller as more is typed. The customer's own
   * line breaks are kept; blank lines too, because they are spacing the customer chose.
   *
   * Never larger than the stored `font_size`, with no floor, so the block always stays inside the
   * box on both axes. Only for long-text fields (`LONG_TEXT_CHAR_LIMIT`): a name keeps the
   * constant-size pass and is never broken across rows.
   *
   * Widths are measured once per word at the stored size and scaled, because text width scales
   * linearly with font size, then the chosen rows are re-measured whole so kerning across spaces
   * cannot push a row past the edge.
   *
   * @returns {{lines: string[], fit: object}|null} null when the placement does not wrap
   */
  function wrapToBox(placement, box, paragraphs, measure, measureCapHeight, trackingEm, charLimit, lineHeight) {
    if (placement.fit === 'none' || !(charLimit > LONG_TEXT_CHAR_LIMIT) || typeof measure !== 'function') return null;

    const boxWidth = maxLineWidth(placement, box);
    if (!(boxWidth > 0) || !(box.height > 0)) return null;

    const nominal = normalisedEmSize(placement, box, measureCapHeight);
    if (!(nominal > 0)) return null;

    const spacingFixed = box.letterSpacing;
    const tracking = num(trackingEm, 0);
    const pitch = lineHeight || LINE_HEIGHT;
    const widthAt = function (text, size, measured) {
      const gaps = Math.max(text.length - 1, 0);
      return measured * (size / nominal) + gaps * (tracking * size + spacingFixed);
    };

    const wordWidths = {};
    const measureWord = function (word) {
      if (!Object.prototype.hasOwnProperty.call(wordWidths, word)) wordWidths[word] = measure(word, nominal);
      return wordWidths[word];
    };
    const spaceWidth = measure(' ', nominal);

    // A word wider than the box, cut into the longest pieces that fit. Letter spacing that does not
    // scale (`spacingFixed`) can make a long enough word too wide at ANY size, so shrinking alone
    // would end at zero and the message would vanish.
    const splitWord = function (word, size) {
      const pieces = [];
      let piece = '';
      let measured = 0;
      Array.from(word).forEach(function (char) {
        const charWidth = measureWord(char);
        if (piece && widthAt(piece + char, size, measured + charWidth) > boxWidth) {
          pieces.push(piece);
          piece = '';
          measured = 0;
        }
        piece += char;
        measured += charWidth;
      });
      if (piece) pieces.push(piece);
      return pieces;
    };

    const wrapAt = function (size, breakWords) {
      const rows = [];
      paragraphs.forEach(function (paragraph) {
        let words = paragraph.split(/\s+/).filter(Boolean);
        if (!words.length) {
          rows.push('');
          return;
        }
        if (breakWords) {
          words = words.reduce(function (all, word) {
            return all.concat(widthAt(word, size, measureWord(word)) > boxWidth ? splitWord(word, size) : [word]);
          }, []);
        }
        let row = words[0];
        let measured = measureWord(row);
        for (let i = 1; i < words.length; i++) {
          const next = row + ' ' + words[i];
          const nextMeasured = measured + spaceWidth + measureWord(words[i]);
          if (widthAt(next, size, nextMeasured) > boxWidth) {
            rows.push(row);
            row = words[i];
            measured = measureWord(row);
          } else {
            row = next;
            measured = nextMeasured;
          }
        }
        rows.push(row);
      });
      return rows;
    };

    const fitsAt = function (size, breakWords) {
      const rows = wrapAt(size, breakWords);
      if (blockHeightEm(rows.length, pitch) * size > box.height) return null;
      const tooWide = rows.some(function (row) {
        return row && widthAt(row, size, measure(row, nominal)) > boxWidth;
      });
      return tooWide ? null : rows;
    };

    // Fewer, narrower rows as the size drops, so "fits" flips exactly once: bisect for it.
    // Rounded DOWN to the precision computeRender reports, so its rounding cannot push a row that
    // exactly fills the box past the edge. 0 means nothing fits.
    const largestFit = function (breakWords) {
      let lo = 0;
      let hi = nominal;
      for (let i = 0; i < 30; i++) {
        const mid = (lo + hi) / 2;
        if (fitsAt(mid, breakWords)) lo = mid;
        else hi = mid;
      }
      return Math.floor(lo * 1000) / 1000;
    };

    let size = nominal;
    let breakWords = false;
    let lines = fitsAt(size, false);
    if (!lines) {
      size = largestFit(false);
      // Whole words cannot fit at any size, so break the overlong one across rows rather than
      // drawing at zero and reporting that it fits.
      if (!(size > 0)) {
        breakWords = true;
        size = largestFit(true);
      }
      if (!(size > 0)) return null;
      lines = wrapAt(size, breakWords);
    }

    const textWidth = lines.reduce(function (widest, row) {
      return row ? Math.max(widest, widthAt(row, size, measure(row, nominal))) : widest;
    }, 0);

    return {
      lines: lines,
      fit: {
        fontSize: size,
        scale: size / nominal,
        fits: true,
        clamped: false,
        textWidth: textWidth,
        limitedByWidth: false,
        emFromHeight: nominal,
        limitedByHeight: size < nominal,
        wrapped: true,
      },
    };
  }

  /** Each line's baseline y, so the block as a whole honours `vertical_align` around the anchor. */
  function lineOffsets(lineCount, lineHeight, verticalAlign) {
    let first = -((lineCount - 1) / 2) * lineHeight;
    if (verticalAlign === 'top') first = 0;
    else if (verticalAlign === 'bottom') first = -(lineCount - 1) * lineHeight;

    const offsets = [];
    for (let i = 0; i < lineCount; i++) offsets.push(first + i * lineHeight);
    return offsets;
  }

  function computeRender(o) {
    const img = o.image;
    const W = num(img && img.width, 0);
    const H = num(img && img.height, 0);

    // A step can carry several placements: a name and a date, or a name and an embroidery
    // design. `placement` stays supported as the single-placement shorthand.
    const placements = Array.isArray(o.placements) && o.placements.length
      ? o.placements
      : (o.placement ? [o.placement] : []);

    const vb = o.collapsed
      ? collapsedViewBox(W, H, o.focus, o.collapsedAspect)
      : { x: 0, y: 0, width: W, height: H };

    const layers = [];
    const designUrls = o.designUrls || {};

    placements.forEach(function (placement, index) {
      const box = placementBox(placement || {}, W, H);

      if (placement && placement.type === 'design') {
        const overlay = designOverlay(placement, box, designUrls[placement.option_key]);
        if (overlay) {
          overlay.index = index;
          overlay.optionKey = placement.option_key || null;
          layers.push(overlay);
        }
        return;
      }

      const typed = textForPlacement(placement, o);
      if (!typed) return;

      // A curved baseline is a single path, so a multi-line message on one is drawn as one line.
      const arced = num(placement && placement.arc, 0) !== 0;
      const typedLines = arced ? [splitLines(typed).filter(Boolean).join(' ')] : splitLines(typed);

      // Per product, from the config's `preview.method`, which Engine reads from the product's
      // routing, unless `o.styleFor` gives the placement its own component's method (a bundle
      // group shot). Where the routing is composite ("Emb + Flock") Engine maps it to the method
      // used for the TEXT, because the artwork is already part of the blank photograph.
      const style = textStyleFor(placement, o);
      const method = style.method;
      const treatment = treatmentFor(method);
      // Resolved AFTER the method, because its tracking changes how wide the line is and so
      // whether it needs shrinking at all.
      // A placement may carry its own `char_limit`; otherwise it comes from the text line the
      // placement points at. Absent from both, the size falls back to the old per-keystroke
      // shrink, so a caller that never learned about limits keeps working. A limit counts
      // characters on one line, so it says nothing about how wide a broken message is.
      // A long message wraps to fill its box instead; a curved baseline is one path, so it cannot.
      const lineHeight = lineHeightFor(style.measureCapHeight, style.measureInkHeight);
      const wrapped = arced
        ? null
        : wrapToBox(placement || {}, box, typedLines, style.measure, style.measureCapHeight, treatment.tracking, charLimitFor(placement, o), lineHeight);
      const lines = wrapped ? wrapped.lines : typedLines;
      const value = lines.join('\n');
      const charLimit = lines.length > 1 ? 0 : charLimitFor(placement, o);
      // Every line is fitted, with tracking and letter spacing, and the tightest one sets the size.
      const widestFit = wrapped ? null : lines
        .map(function (line) {
          return fitFontSize(placement || {}, box, line, style.measure, style.measureCapHeight, treatment.tracking, charLimit);
        })
        .reduce(function (a, b) {
          return b.fontSize < a.fontSize || (b.fontSize === a.fontSize && b.textWidth > a.textWidth) ? b : a;
        });
      const fit = wrapped ? wrapped.fit : fitLinesToBox(widestFit, placement || {}, box, lines.length, lineHeight);
      const arc = arcPath(placement || {}, box, fit.textWidth);
      const offsets = lineOffsets(lines.length, fit.fontSize * lineHeight, (placement && placement.vertical_align) || 'middle');
      const layer = {
        kind: 'text',
        index: index,
        textLineKey: (placement && placement.text_line_key) || null,
        value: value,
        x: round(box.anchorX),
        y: round(box.anchorY),
        textAnchor: box.textAnchor,
        dominantBaseline: box.dominantBaseline,
        fontFamily: style.fontFamily,
        fontSize: round(fit.fontSize, 3),
        // The placement's own value (fixed, in user units) plus the method's tracking (scaled with
        // the final font size, so a shrunk name keeps its proportions).
        letterSpacing: (box.letterSpacing || treatment.tracking)
          ? round(box.letterSpacing + treatment.tracking * fit.fontSize, 3)
          : null,
        method: method,
        // The colour as DRAWN, which is not always the colour as recorded: see METHOD_TREATMENTS.
        // The cart property carries the colour's code, never this, so nothing downstream is
        // affected by a rendering allowance.
        fill: applyLighten(style.fill, treatment.lighten),
        // Scaled with the FINAL font size, so a long name that shrank gets proportionally finer
        // stitching rather than a pasted-on outline.
        strokeWidth: round(fit.fontSize * treatment.strokeExpansion, 3),
        // Read by callers that set attributes on persistent nodes rather than using `markup`, so
        // they cannot hardcode a different value. See METHOD_TREATMENTS.
        opacity: treatment.opacity,
        // Hinting at a small on-screen size widens glyphs past what `measure` reported, so a row
        // fitted to the box edge drew over it. Unhinted, the drawn width scales with font-size.
        textRendering: 'geometricPrecision',
        transform: box.rotation
          ? 'rotate(' + round(box.rotation, 3) + ' ' + round(box.pivotX) + ' ' + round(box.pivotY) + ')'
          : null,
        fit: fit,
        arc: arc,
        lines: lines.map(function (line, i) {
          return { value: line, y: round(box.anchorY + offsets[i]) };
        }),
      };

      if (arc) {
        // Along a textPath the glyphs are placed relative to startOffset, and arcPath() has
        // already shifted the path so the alignment point lands on the anchor — so every
        // alignment renders from the middle of the path. x/y are dropped: they would fight
        // the path and browsers disagree about which wins.
        layer.x = null;
        layer.y = null;
        layer.textAnchor = 'middle';
        layer.arcStartOffset = '50%';
      }

      layers.push(layer);
    });

    // `text`, `arc` and `fit` describe the FIRST text layer. Kept because a single line is the
    // common case and existing callers read them; anything new should read `layers`.
    const firstText = layers.filter(function (l) { return l.kind === 'text'; })[0] || null;
    const spoken = layers
      .filter(function (l) { return l.kind === 'text'; })
      .map(function (l) { return l.value.replace(/\n+/g, ' '); })
      .join(', ');

    return {
      viewBox: formatViewBox(vb),
      image: { url: (img && img.url) || '', width: W, height: H },
      layers: layers,
      text: firstText,
      arc: firstText ? firstText.arc : null,
      fit: firstText
        ? firstText.fit
        : { fontSize: 0, scale: 1, fits: true, clamped: false, textWidth: 0 },
      ariaLabel: spoken
        ? 'Product preview personalised with "' + spoken + '"'
        : 'Product preview',
    };
  }

  /**
   * Render {@link computeRender}'s result to an SVG string.
   *
   * Not used by the storefront (which binds attributes on persistent nodes instead) — kept
   * because it makes the full output unit-testable and rasterisable offline.
   *
   * @param {object}   o.image           { url, width, height } base image (natural pixels)
   * @param {object}   o.placement       placement from the config
   * @param {string}   o.text            customer's text ('' renders base image only)
   * @param {string}   o.fontFamily      CSS family to render with
   * @param {string}   o.fill            text colour
   * @param {boolean}  o.collapsed       render the cropped pinned band
   * @param {object}   o.focus           { x, y } crop anchor, normalised
   * @param {number}   o.collapsedAspect width/height of the collapsed band
   * @param {function} o.measure         (text, fontSize) => width, in user units
   * @param {function} o.measureInkHeight optional (fontSize) => how far INK_SAMPLE reaches above
   *                                     plus below the baseline, in user units. Sets the row pitch
   *                                     of a multi-line message (see `lineHeightFor`)
   * @param {function} o.styleFor        optional (placement) => { fontFamily, fill, method, measure,
   *                                     measureCapHeight, measureInkHeight }, so each name in a group shot can be
   *                                     drawn in its own component's style
   * @returns {{ markup: string, fit: object, viewBox: string }}
   */
  function buildSvg(o) {
    const r = computeRender(o);

    // An arced baseline is drawn by reference, so it needs an id. Callers pass their own
    // because several previews can share a page; each text layer gets its own suffix so two
    // curved lines in the same step cannot end up on the same path.
    const arcBase = o.arcPathId || 'pim-arc-path';
    const arcIdFor = (layer) => arcBase + '-' + layer.index;

    const defs = r.layers
      .filter((l) => l.kind === 'text' && l.arc)
      .map((l) =>
        '<path id="' + escapeAttr(arcIdFor(l)) + '" d="' + escapeAttr(l.arc.d) + '" fill="none"/>')
      .join('');

    const nodes = r.layers.map((l) => {
      if (l.kind === 'design') {
        return '<image href="' + escapeAttr(l.url) + '"' +
          ' x="' + l.x + '" y="' + l.y + '" width="' + l.width + '" height="' + l.height + '"' +
          ' preserveAspectRatio="' + l.preserveAspectRatio + '"' +
          (l.transform ? ' transform="' + escapeAttr(l.transform) + '"' : '') +
          '/>';
      }

      const inner = l.arc
        ? '<textPath href="#' + escapeAttr(arcIdFor(l)) + '" startOffset="' + l.arcStartOffset + '">' +
          escapeText(l.value) + '</textPath>'
        : l.lines && l.lines.length > 1
          ? l.lines.map((line) =>
            '<tspan x="' + l.x + '" y="' + line.y + '">' + escapeText(line.value) + '</tspan>').join('')
          : escapeText(l.value);

      return '<text' +
        (l.arc ? '' : ' x="' + l.x + '" y="' + l.y + '"') +
        ' text-anchor="' + l.textAnchor + '"' +
        ' dominant-baseline="' + l.dominantBaseline + '"' +
        ' text-rendering="' + l.textRendering + '"' +
        ' font-family="' + escapeAttr(l.fontFamily) + '"' +
        ' font-size="' + l.fontSize + '"' +
        (l.letterSpacing ? ' letter-spacing="' + l.letterSpacing + '"' : '') +
        ' fill="' + escapeAttr(l.fill) + '"' +
        // Same 90% the storefront applies, for the same reason the stroke is here: any surface
        // drawing at full strength would show a merchandiser a colour the customer never gets.
        (l.opacity != null && l.opacity !== 1 ? ' opacity="' + l.opacity + '"' : '') +
        // Same satin-stitch weight the storefront applies imperatively. It has to be here too, or
        // the admin editor would draw a lighter letterform than the customer sees, which is the
        // precise kind of disagreement this shared module exists to stop.
        (l.strokeWidth
          ? ' stroke="' + escapeAttr(l.fill) + '" stroke-width="' + l.strokeWidth + '"' +
            ' stroke-linejoin="round" stroke-linecap="round" paint-order="stroke fill"'
          : '') +
        (l.transform ? ' transform="' + escapeAttr(l.transform) + '"' : '') +
        '>' + inner + '</text>';
    }).join('');

    const markup =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="' + r.viewBox + '"' +
      ' width="100%" height="100%" role="img" aria-label="' + escapeAttr(r.ariaLabel) + '">' +
      (defs ? '<defs>' + defs + '</defs>' : '') +
      // The base image is optional. Engine's editor lays this SVG over its own <img> so the
      // photograph is not re-decoded on every pointer move; it passes width and height with no
      // url, which is all the viewBox needs. It used to pass a 1x1 transparent gif to satisfy
      // this line. The storefront does pass a url, so it draws the photo here.
      (r.image && r.image.url
        ? '<image href="' + escapeAttr(r.image.url) + '" x="0" y="0"' +
          ' width="' + r.image.width + '" height="' + r.image.height + '"' +
          ' preserveAspectRatio="xMidYMid slice"/>'
        : '') +
      nodes +
      '</svg>';

    return { markup, fit: r.fit, viewBox: r.viewBox, arc: r.arc, layers: r.layers, render: r };
  }

  function round(n, dp) {
    const f = Math.pow(10, dp == null ? 2 : dp);
    return Math.round(num(n, 0) * f) / f;
  }

  return {
    MIN_SHRINK_RATIO,
    STITCH_EXPANSION,
    METHOD_TREATMENTS,
    DEFAULT_METHOD,
    treatmentFor,
    applyLighten,
    resolveViews,
    resolveView,
    resolveFontChoice,
    resolveFill,
    ARC_LIMIT,
    REFERENCE_CAP_RATIO,
    LINE_HEIGHT,
    INK_SAMPLE,
    ROW_GAP_CAP,
    lineHeightFor,
    blockHeightEm,
    LONG_TEXT_CHAR_LIMIT,
    splitLines,
    wrapToBox,
    lineOffsets,
    capHeightRatio,
    normalisedEmSize,
    FIT_REFERENCE_ALPHABET,
    charLimitsFrom,
    constantEmSize,
    resolveEmSize,
    anchorOffset,
    clamp,
    round,
    resolveStep,
    resolveBaseImage,
    textAnchor,
    dominantBaseline,
    placementBox,
    maxLineWidth,
    arcPath,
    arcCircle,
    arcPointAt,
    designOverlay,
    textForPlacement,
    charLimitFor,
    fitFontSize,
    fitFontSizeRaw,
    collapsedViewBox,
    defaultFocus,
    formatViewBox,
    escapeText,
    escapeAttr,
    computeRender,
    buildSvg,
  };
});
